/**
 * Suspire — GA4 ecommerce list/cart tracking
 *
 * Emits three GA4 ecommerce events the storefront was missing:
 *   • select_item     — click a product in any list (Boost, Argoid, native grids)
 *   • view_item_list  — a product list scrolls into view; products added later (load more, a tab
 *                       opening) are sent in a follow-up batch, each product once per page
 *   • view_cart       — the cart page loads (complete data from /cart.js)
 *
 * Design:
 *  - Delegated / observer-based — survives Boost PFS & Argoid client-side re-renders.
 *  - Reads EXISTING tile DOM only; does NOT edit any card template.
 *  - Pushes to dataLayer; GTM "GA4 Event" tags forward to G-1Z63M5ZR43.
 *  - select_item fires on mousedown (before navigation) so its hit reliably sends,
 *    de-duplicated against the follow-up click.
 *  - Wrapped so it can never throw and break navigation/rendering.
 *
 * item_id matches the app's view_item format: shopify_IN_<productId>_<variantId>.
 */
(function () {
  'use strict';
  if (window.__ga4ListTrackingBound) return;
  window.__ga4ListTrackingBound = true;
  var dl = window.dataLayer = window.dataLayer || [];

  function txt(el) { return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : ''; }
  function parsePrice(s) { if (!s) return undefined; var m = ('' + s).match(/(\d[\d,]*(?:\.\d+)?)/); if (!m) return undefined; var n = parseFloat(m[1].replace(/,/g, '')); return isFinite(n) ? n : undefined; }
  function push(event, ecommerce) { dl.push({ ecommerce: null }); dl.push({ event: event, ecommerce: ecommerce }); }
  function now() { return (window.Date && Date.now) ? Date.now() : (+new Date()); }

  function findTile(a) { return (a.closest && a.closest('[data-product-id], [data-product-handle], .grid-product, .grid__item, .product-item')) || a.parentElement; }

  function listContext(el) {
    var withList = el && el.closest && el.closest('[data-ga-list-name]');
    if (withList) return { name: withList.getAttribute('data-ga-list-name'), id: withList.getAttribute('data-ga-list-id') || undefined };
    var section = el && el.closest && el.closest('.shopify-section, section');
    if (section) { var n = txt(section.querySelector('h1, h2, h3, .section-title, .title, .carousel-title')); if (n) return { name: n.slice(0, 100) }; }
    var p = location.pathname;
    if (/^\/collections\//.test(p)) return { name: 'Collection: ' + decodeURIComponent((p.split('/')[2] || '').replace(/-/g, ' ')) };
    if (/^\/search/.test(p)) return { name: 'Search results' };
    if (p === '/' || p === '') return { name: 'Home' };
    return { name: 'Product list' };
  }

  function readItem(tile, a) {
    if (!tile) return null;
    var pid = (tile.getAttribute && tile.getAttribute('data-product-id')) || '';
    if (!pid && tile.querySelector) { var w = tile.querySelector('[data-product-id]'); if (w) pid = w.getAttribute('data-product-id') || ''; }
    if (!pid) return null;
    var vEl = tile.querySelector && tile.querySelector('[data-variant-id]');
    var vid = vEl ? (vEl.getAttribute('data-variant-id') || '') : '';
    var item = { item_id: 'shopify_IN_' + pid + (vid ? '_' + vid : '') };
    var name = txt(tile.querySelector && tile.querySelector('.grid-product__title, .product-item__title, h3, h2')) || (a && a.getAttribute && a.getAttribute('aria-label')) || (a && txt(a));
    if (name) item.item_name = name;
    var price = parsePrice(txt(tile.querySelector && tile.querySelector('.grid-product__price, .price')));
    if (price !== undefined) item.price = price;
    var brand = (tile.getAttribute && tile.getAttribute('data-product-brand')) || txt(tile.querySelector && tile.querySelector('.grid-product__vendor'));
    if (brand) item.item_brand = brand;
    return item;
  }

  /* ---------- select_item (mousedown + deduped click, so the hit sends before navigation) ---------- */
  var lastSel = { a: null, t: 0 };
  function onSelect(e) {
    try {
      var a = e.target && e.target.closest && e.target.closest('a[href*="/products/"]');
      if (!a) return;
      var t = now();
      if (lastSel.a === a && (t - lastSel.t) < 1500) return; // dedup mousedown+click
      lastSel = { a: a, t: t };
      var tile = findTile(a);
      var item = readItem(tile, a);
      if (!item) return;
      var ctx = listContext(tile);
      if (ctx.name) item.item_list_name = ctx.name;
      if (tile && tile.parentElement) { var idx = Array.prototype.indexOf.call(tile.parentElement.children, tile); if (idx >= 0) item.index = idx + 1; }
      push('select_item', { item_list_name: ctx.name || undefined, items: [item] });
    } catch (err) { /* never break navigation */ }
  }
  document.addEventListener('mousedown', onSelect, true);
  document.addEventListener('click', onSelect, true);

  /* ---------- view_item_list (impression; only real product cards) ---------- */
  function realTiles(scope) {
    if (!scope || !scope.querySelectorAll) return [];
    return Array.prototype.filter.call(scope.querySelectorAll('[data-product-id]'), function (t) { return t.querySelector && t.querySelector('a[href*="/products/"]'); });
  }
  // A list is the nearest element marked data-ga-list-scope (e.g. a featured strip inside a grid),
  // else the section. Only products that are rendered count (a hidden copy or tab never does).
  function containerOf(tile) { return tile.closest('[data-ga-list-scope]') || tile.closest('.shopify-section') || tile.closest('[class*="argoid"]') || tile.parentElement; }
  function listTiles(container) {
    return realTiles(container).filter(function (t) { return containerOf(t) === container && t.getClientRects().length > 0; });
  }

  var tileObserved = ('WeakSet' in window) ? new WeakSet() : null;
  var tileReported = ('WeakSet' in window) ? new WeakSet() : null;
  var itemReported = {}; // "list|item_id": a product re-drawn in place (e.g. the cart updating) is not seen twice
  var io = ('IntersectionObserver' in window) ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      if (!en.isIntersecting) return;
      io.unobserve(en.target);
      try {
        // The first product seen in a list reports every rendered product of that list; products
        // added later (load more, a tab opening) go out as a follow-up batch. Positions count across
        // the whole list, so a second batch continues from the first.
        var container = containerOf(en.target);
        if (!container) return;
        var tiles = listTiles(container);
        if (!tiles.length) return;
        var ctx = listContext(tiles[0]);
        var items = [];
        tiles.forEach(function (t, i) {
          if (tileReported && tileReported.has(t)) return;
          var it = readItem(t, t.querySelector('a[href*="/products/"]'));
          if (!it) return;
          if (tileReported) tileReported.add(t);
          var key = (ctx.name || '') + '|' + it.item_id;
          if (itemReported[key]) return;
          itemReported[key] = true;
          it.index = i + 1;
          items.push(it);
        });
        if (!items.length) return;
        push('view_item_list', { item_list_name: ctx.name || undefined, items: items });
      } catch (err) { }
    });
  }, { threshold: 0.3 }) : null;

  function scanLists() {
    if (!io) return;
    try { realTiles(document).forEach(function (t) { if (tileObserved && !tileObserved.has(t)) { tileObserved.add(t); io.observe(t); } }); } catch (err) { }
  }

  /* ---------- view_cart (cart page, complete data) ---------- */
  function trackViewCart() {
    if (!/^\/cart(\/|$|\?)/.test(location.pathname)) return;
    try {
      fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (!cart || !cart.items || !cart.items.length) return;
          var items = cart.items.map(function (li, i) {
            var it = { item_id: 'shopify_IN_' + li.product_id + '_' + li.variant_id, item_name: li.product_title || li.title, quantity: li.quantity, index: i + 1 };
            if (li.price != null) it.price = li.price / 100;
            if (li.vendor) it.item_brand = li.vendor;
            if (li.product_type) it.item_category = li.product_type;
            if (li.variant_title) it.item_variant = li.variant_title;
            return it;
          });
          push('view_cart', { currency: cart.currency || 'INR', value: (cart.total_price || 0) / 100, items: items });
        }).catch(function () { });
    } catch (err) { }
  }

  /* ---------- init + watch for client-side re-renders ---------- */
  function init() { scanLists(); trackViewCart(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();

  if ('MutationObserver' in window) {
    var scanScheduled = false;
    var mo = new MutationObserver(function () {
      if (scanScheduled) return;
      scanScheduled = true;
      setTimeout(function () { scanScheduled = false; scanLists(); }, 1000);
    });
    try { mo.observe(document.body, { childList: true, subtree: true }); } catch (err) { }
  }
})();
